// Employee/CalendarApp.tsx
import React, {useCallback, useContext, useEffect, useMemo, useState,} from "react";
import {Container} from "@mui/material";
import {ViewHeader} from "./components/View/ViewHeader";
import EventEditModal from "./components/View/EventEditModal";
import WeekView from "./Views/WeekView";
import DayView from "./Views/DayView";
import MonthView from "./Views/MonthView";
import {AppState, useAppState} from "./hooks/useAppState";
import {useEvents} from "./hooks/useEvents";
import {axios} from "Auth/axiosConfig";
import {endOfISOWeek, format, startOfISOWeek} from "date-fns";
import {AuthContext, AuthContextType} from "Auth/AuthContext";
import {useProfile} from "User/ProfileContext";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import moment, {Moment} from "moment";
import SlotConfirmationModal from "./components/View/SlotConfirmationModal";
import pprint from "../../Utils/pprint";
import {IPsychologist} from "../../Types/psychology/psychologist";
import {AvailabilityInterval, Consultation} from "Employee/types";

interface CalendarAppProps {
    initialAppState: AppState;
    psychologist?: IPsychologist;
    duration?: number;
    onConfirmSlot?: (slot: Moment) => void;
}

function CalendarApp({
                         initialAppState,
                         psychologist,
                         duration,
                         onConfirmSlot,
                     }: CalendarAppProps) {
    const {isAuthenticated} = useContext(AuthContext) as AuthContextType;
    const {byResponse} = useErrorProcessing();
    const {selectedProfile} = useProfile();

    const [consultations, setConsultations] = useState<Consultation[]>([]);
    const [availabilityIntervals, setAvailabilityIntervals] = useState<
        AvailabilityInterval[]
    >([]);
    const [currentWeek, setCurrentWeek] = useState<Moment>(
        moment().startOf("isoWeek")
    );

    const {
        currentView,
        setView,
        currentWeekStart,
        setCurrentWeekStart,
        userType,
        toggleUserType,
        canEdit,
        availableViews,
        setAvailableViews,
        showButtons,
        debugging,
        timeZone,
        setTimeZone,
        showHorizontalLines,
        setShowHorizontalLines,
        showVerticalLines,
        setShowVerticalLines,
        showLineToggles,
        setShowLineToggles,
        currentViewRange,
        updateViewRange,
        maxDaysForSelection,
        setMaxDaysForSelection,
        showOnlyEvents,
        setShowOnlyEvents,
        fetchMode,
        setFetchMode,
        refetchTrigger,
    } = useAppState(initialAppState);

    const {
        events,
        setEvents,
        editingEvent,
        isModalOpen,
        setIsModalOpen,
        handleEventEdit,
        handleEventAdd,
        handleEventSave,
        closeModal,
        pendingEventAdd,
        setPendingEventAdd,
        addEvent,
        onRemove,
        handleCreateAvailability,
        changeTimeZone,
    } = useEvents(
        userType,
        canEdit,
        consultations,
        availabilityIntervals,
        timeZone
    );
    const {user} = useContext(AuthContext) as AuthContextType;
    const [isModalSlotOpen, setIsModalSlotOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<Moment | null>(null); // Selected slot state

    const handleSlotSelected = useCallback((slot: Moment) => {
        setSelectedSlot(slot);
        setIsModalSlotOpen(true); // Open the modal when a slot is selected
    }, []);

    const closeModalSlot = useCallback(() => {
        setIsModalOpen(false);
        setSelectedSlot(null);
    }, []);

    const confirmSlot = useCallback(() => {
        if (onConfirmSlot && selectedSlot) {
            onConfirmSlot(selectedSlot);
        }
        closeModalSlot();
    }, [onConfirmSlot, selectedSlot, closeModal]);

    // Memoize the formatted start and end dates to prevent unnecessary re-renders
    const formattedDates = useMemo(() => {
        if (!currentViewRange || !currentViewRange.start || !currentViewRange.end)
            return null;
        return {
            start: format(currentViewRange.start, "yyyy-MM-dd"),
            end: format(currentViewRange.end, "yyyy-MM-dd"),
        };
    }, [currentViewRange]);

    // Memoize the API fetching logic to prevent unnecessary re-renders
    const fetchConsultationsOrAvailability = useCallback(() => {
        if (!formattedDates) return;
        if (!isAuthenticated) return;

        const {start, end} = formattedDates;

        // Create dates using the formatted strings
        const startDate = new Date(start);
        const endDate = new Date(end);

        // Ensure valid dates before converting to ISO string
        const startWeek = startOfISOWeek(startDate);
        const endWeek = endOfISOWeek(endDate);

        // Convert to ISO strings only after ensuring valid dates
        const startWeekISO = startWeek.toISOString();
        const endWeekISO = endWeek.toISOString();

        // If a psychologist is selected, fetch availability for that psychologist
        if (fetchMode === "psychologist" && psychologist?.id) {
            if (!duration) return;

            axios
                .get(
                    `/api/v1/psychologist/${psychologist.id}/availability/?start=${startWeekISO}&end=${endWeekISO}&duration=${duration}`
                )
                .then((response) => {
                    pprint("Psychologist availability intervals");
                    setAvailabilityIntervals(response.data);
                })
                .catch((error) => byResponse(error));
        } else {
            // Fetch consultations or availability for the current user
            const consultationUrl =
                selectedProfile === "psychologist"
                    ? `/api/v1/psychologist/consultations/?start=${start}&end=${end}`
                    : `/api/v1/client/consultations/?start=${start}&end=${end}`;

            if (showOnlyEvents) {
                axios
                    .get(consultationUrl)
                    .then((response) => {
                        pprint(`Consultations for ${selectedProfile}`);
                        setConsultations(response.data);
                    })
                    .catch((error) => byResponse(error));
            } else if (fetchMode === "current-user") {
                // Fetch availability intervals for the current user
                axios
                    .get(`/api/v1/employee/availability/?start=${start}&end=${end}`)
                    .then((response) => {
                        pprint("User availability intervals");
                        setAvailabilityIntervals(response.data);
                    })
                    .catch((error) => byResponse(error));
            }
        }
    }, [
        formattedDates,
        duration,
        isAuthenticated,
        psychologist,
        selectedProfile,
        showOnlyEvents,
        byResponse,
        timeZone,
        fetchMode,
    ]);

    useEffect(() => {
        if (formattedDates) {
            fetchConsultationsOrAvailability();
        }
    }, [fetchConsultationsOrAvailability, refetchTrigger]);

    function renderCurrentView() {
        const commonProps = {
            events,
            setEvents,
            editingEvent,
            handleEventEdit,
            canEdit,
            userType,
            showButtons,
            timeZone,
            user,
        };

        switch (currentView) {
            case "week":
                return (
                    <WeekView
                        {...commonProps}
                        currentWeekStart={currentWeekStart}
                        toggleUserType={toggleUserType}
                        handleEventAdd={handleEventAdd}
                        onRemove={onRemove}
                        handleCreateAvailability={handleCreateAvailability}
                        showHorizontalLines={showHorizontalLines}
                        showVerticalLines={showVerticalLines}
                        maxDaysForSelection={maxDaysForSelection}
                        onConfirmSlot={handleSlotSelected}
                    />
                );
            case "day":
                return (
                    <DayView
                        {...commonProps}
                        currentDay={currentWeekStart}
                        onEdit={handleEventEdit}
                        handleEventAdd={handleEventAdd}
                        onCreateAvailability={handleCreateAvailability}
                    />
                );
            case "month":
                return (
                    <MonthView {...commonProps} currentMonthStart={currentWeekStart}/>
                );
            default:
                return null;
        }
    }

    return (
        <Container maxWidth={false} className={"p-0 h-100 overflow-hidden"}>
            <div className={"fc h-100"}>
                <ViewHeader
                    currentView={currentView}
                    setView={(view) => {
                        setView(view);
                        updateViewRange(view, currentWeekStart);
                    }}
                    currentWeekStart={currentWeekStart}
                    setCurrentWeekStart={(date: Date) => {
                        setCurrentWeekStart(date);
                        updateViewRange(currentView, date);
                    }}
                    userType={userType}
                    toggleUserType={toggleUserType}
                    availableViews={availableViews}
                    setAvailableViews={setAvailableViews}
                    debugging={debugging}
                    timeZone={timeZone}
                    setTimeZone={(newTimeZone) => {
                        setTimeZone(newTimeZone);
                        changeTimeZone(newTimeZone);
                    }}
                    showHorizontalLines={showHorizontalLines}
                    setShowHorizontalLines={setShowHorizontalLines}
                    showVerticalLines={showVerticalLines}
                    setShowVerticalLines={setShowVerticalLines}
                    showLineToggles={showLineToggles}
                    showOnlyEvents={showOnlyEvents}
                    setShowOnlyEvents={setShowOnlyEvents}
                    user={user ? {timezone: user.timezone} : undefined}
                />
                <div className={"d-grid h-100 overflow-auto"}>
                    {renderCurrentView()}
                </div>
            </div>
            {selectedSlot && (
                <SlotConfirmationModal
                    isModalOpen={isModalSlotOpen}
                    setIsModalOpen={setIsModalSlotOpen}
                    onConfirm={confirmSlot}
                    selectedSlot={selectedSlot}
                />
            )}

            <EventEditModal
                event={editingEvent}
                open={isModalOpen}
                onClose={() => {
                    closeModal();
                    setPendingEventAdd(null);
                }}
                onSave={handleEventSave}
                userType={userType}
                onAdd={handleEventAdd}
                pendingEventAdd={pendingEventAdd}
                addEvent={addEvent}
                isMonthView={currentView === "month"}
                onDelete={onRemove}
                timeZone={timeZone}
            />
        </Container>
    );
}

export default CalendarApp;
